import { takeLatest, call, put, select } from "redux-saga/effects";
import { postPersonalInformation, getPersonalInformation, getShippingInformation, postShippingInformation, postCartToPayment, getOrderInformation } from "@montana/services/checkout";
import { CheckoutActionTypes, ISetCustomerInformation } from ".";
import { setCustomerInformationSuccess, loadCustomerInformationSuccess, loadShippingInformationSuccess, ISetShippingInformation, setShippingInformationSuccess, loadOrderInformationSuccess } from "./checkout.actions";
import { IStoreState } from "@montana/store/store";
import { ICartContainerState } from "../cart";
import { ICart, ICustomerInformation, IOrderSummary, IShippingInformation } from "@montana/models/generated/cms.interface";
import { pushNotification } from "../notification/notification.actions";
import { NotificationLevel } from "../notification";
import { trackCheckoutStep, trackCheckoutOption } from "../tracking/tracking.actions";
import {ICheckoutContainerState, ICreatePayment} from "./checkout";
import { updateCart } from "../cart/cart.actions";
import { OmitConfig } from "@montana/models/dev";
import { connectServiceFunctions } from "../base/base.sagas";

function* onSetCustomerInformation({ customerInformation }: ISetCustomerInformation) {
    try {
        const { cartGuid }: ICartContainerState = (yield select((store: IStoreState) => store.cart) as unknown as ICartContainerState);
        if (!cartGuid) return;

        const [cPostPersonalInformation]: [OmitConfig<typeof postPersonalInformation>] = yield connectServiceFunctions(postPersonalInformation);
        const response: Response = yield call(cPostPersonalInformation, cartGuid, customerInformation);

        const { ok } = response;

        if (ok) {
            yield put(trackCheckoutOption(1, 'guest'));
            yield put(setCustomerInformationSuccess(customerInformation));
        } else {
            console.error('Failed to post checkout personal information');
        }

    } catch (error) {
        console.error('Failed to post checkout personal information', error);
    }
}

function* onLoadCustomerInformation() {
    try {
        const { cartGuid }: ICartContainerState = (yield select((store: IStoreState) => store.cart) as unknown as ICartContainerState);
        if (!cartGuid) return;

        const [cGetPersonalInformation]: [OmitConfig<typeof getPersonalInformation>] = yield connectServiceFunctions(getPersonalInformation);
        const response: Response = yield call(cGetPersonalInformation, cartGuid);

        const { ok, json } = response;

        if (ok) {
            const customer: ICustomerInformation = yield call(json.bind(response));
            yield put(loadCustomerInformationSuccess(customer));
        } else {
            console.error('Failed to get checkout personal information');
        }
    } catch (error) {
        console.error('Failed to get checkout personal information', error);
    }
}

function* onLoadShippingInformation() {
    try {
        const { cartGuid }: ICartContainerState = (yield select((store: IStoreState) => store.cart) as unknown as ICartContainerState);
        if (!cartGuid) return;

        const [cGetShippingInformation]: [OmitConfig<typeof getShippingInformation>] = yield connectServiceFunctions(getShippingInformation);
        const response: Response = yield call(cGetShippingInformation, cartGuid);

        const { ok, json } = response;

        if (ok) {
            const shipping: IShippingInformation[] = yield call(json.bind(response));
            yield put(loadShippingInformationSuccess(shipping));
        } else {
            console.error('Failed to get checkout shipping information');
        }
    } catch (error) {
        console.error('Failed to get checkout shipping information', error);
    }
}

function* onSetShippingInformation({ shippingInformation, finalize }: ISetShippingInformation) {
    try {
        const { cartGuid }: ICartContainerState = (yield select((store: IStoreState) => store.cart));
        if (!cartGuid) return;

        const [cPostShippingInformation]: [OmitConfig<typeof postShippingInformation>] = yield connectServiceFunctions(postShippingInformation);
        const response: Response = yield call(cPostShippingInformation, cartGuid, shippingInformation);
        const { json, ok } = response;

        if (ok) {
            if (finalize) {
                yield put(setShippingInformationSuccess());
            } else {
                const cart: ICart = yield call(json.bind(response));

                yield put(updateCart(cart));
            }
        } else {
            console.error('Failed to post checkout shipping information');
        }
    } catch (error) {
        console.error('Failed to post checkout shipping information', error);
    }
}

function* onSetShippingInformationSuccess() {
    try {
        const { cartGuid }: ICartContainerState = (yield select((store: IStoreState) => store.cart) as unknown as ICartContainerState);
        const { selectedShippingInformation }: ICheckoutContainerState = (yield select((store: IStoreState) => store.checkout) as unknown as ICheckoutContainerState);

        if (!cartGuid) return;

        const [cPostCartToPayment]: [OmitConfig<typeof postCartToPayment>] = yield connectServiceFunctions(postCartToPayment);
        const response: Response = yield call(cPostCartToPayment, cartGuid);
        const { json, ok } = response;

        if (ok) {
            const payment: ICreatePayment = yield call(json.bind(response));
            if (payment.redirectUrl) {
                yield put(trackCheckoutOption(2, selectedShippingInformation?.title || ''));
                yield put(trackCheckoutStep(3));
                location.href = payment.redirectUrl;
            } else {
                yield put(pushNotification('Failed to redirect to payment', NotificationLevel.ERROR));
            }
        } else {
            console.error('Failed to post cart to payment');
            yield put(pushNotification('Failed to redirect to payment', NotificationLevel.ERROR));
        }
    } catch (error) {
        console.error('Failed to post cart to payment', error);
        yield put(pushNotification('Failed to redirect to payment', NotificationLevel.ERROR));
    }
}

export function* onLoadOrderInformation(orderId: string) {
    try {
        const [cGetOrderInformation]: [OmitConfig<typeof getOrderInformation>] = yield connectServiceFunctions(getOrderInformation);
        const response: Response = yield call(cGetOrderInformation, orderId);

        const { json, ok } = response;

        if (ok) {
            const orderInformation: IOrderSummary = yield call(json.bind(response));
            yield put(loadOrderInformationSuccess(orderInformation));
        } else {
            console.error('Failed to load order information');
        }
    } catch (error) {
        console.error('Failed to load order information', error);
    }
}

export const checkoutSagas = [
    takeLatest(CheckoutActionTypes.SET_CUSTOMER_INFORMATION, onSetCustomerInformation),
    takeLatest(CheckoutActionTypes.LOAD_CUSTOMER_INFORMATION, onLoadCustomerInformation),
    takeLatest(CheckoutActionTypes.LOAD_SHIPPING_INFORMATION, onLoadShippingInformation),
    takeLatest(CheckoutActionTypes.SET_SHIPPING_INFORMATION, onSetShippingInformation),
    takeLatest(CheckoutActionTypes.SET_SHIPPING_INFORMATION_SUCCESS, onSetShippingInformationSuccess),
];
